import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import './GradeSubmissions.css';

const GradeSubmissions = () => {
  const { assignmentId } = useParams();
  const { user } = useAuth();

  const [assignment, setAssignment] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [gradingSubmission, setGradingSubmission] = useState(null);
  const [gradeForm, setGradeForm] = useState({ score: '', feedback: '' });
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch assignment details
        const assignmentResponse = await api.get(`/assignments/${assignmentId}`);
        setAssignment(assignmentResponse.data);

        // Fetch all submissions
        const submissionsResponse = await api.get(`/assignments/${assignmentId}/submissions`);
        setSubmissions(submissionsResponse.data);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err.response?.data?.error || 'Failed to load submissions');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [assignmentId]);

  const handleGradeClick = (submission) => {
    setGradingSubmission(submission);
    setGradeForm({
      score: submission.score || '',
      feedback: submission.feedback || '',
    });
    setSuccessMessage(null);
  };

  const handleGradeSubmit = async (e) => {
    e.preventDefault();

    if (!gradeForm.score || gradeForm.score < 0 || gradeForm.score > assignment.max_score) {
      setError(`Score must be between 0 and ${assignment.max_score}`);
      return;
    }

    try {
      setSaving(true);
      setError(null);

      await api.put(`/assignments/submissions/${gradingSubmission.id}/grade`, {
        score: parseFloat(gradeForm.score),
        feedback: gradeForm.feedback,
      });

      // Update local state
      setSubmissions(submissions.map(s =>
        s.id === gradingSubmission.id
          ? {
              ...s,
              score: parseFloat(gradeForm.score),
              feedback: gradeForm.feedback,
              status: 'graded',
              graded_at: new Date().toISOString(),
              grader_name: `${user.first_name} ${user.last_name}`,
            }
          : s
      ));

      setSuccessMessage('Grade saved successfully!');
      setGradingSubmission(null);
    } catch (err) {
      console.error('Error saving grade:', err);
      setError(err.response?.data?.error || 'Failed to save grade');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelGrade = () => {
    setGradingSubmission(null);
    setGradeForm({ score: '', feedback: '' });
    setError(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStatusStats = () => {
    const pending = submissions.filter(s => s.status === 'submitted').length;
    const graded = submissions.filter(s => s.status === 'graded').length;
    return { pending, graded, total: submissions.length };
  };

  if (loading) return <div className="loading">Loading submissions...</div>;
  if (error && !assignment) return <div className="error-state">{error}</div>;

  const stats = getStatusStats();

  return (
    <div className="page grade-submissions-page">
      <div className="container">
        <section className="submissions-hero">
          <div className="hero-content">
            <Link to={`/courses/${assignment?.course_id}/assignments`} className="back-link">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M15 19l-7-7 7-7" />
              </svg>
              Back to Assignments
            </Link>
            <p className="eyebrow">Grading</p>
            <h1>{assignment?.title}</h1>
            <p>{assignment?.course_title}</p>
          </div>
          <div className="hero-stats">
            <div className="stat-card">
              <span className="label">Total</span>
              <p className="value">{stats.total}</p>
            </div>
            <div className="stat-card pending">
              <span className="label">Pending</span>
              <p className="value">{stats.pending}</p>
            </div>
            <div className="stat-card graded">
              <span className="label">Graded</span>
              <p className="value">{stats.graded}</p>
            </div>
          </div>
        </section>

        {/* Success Message */}
        {successMessage && (
          <div className="alert alert-success">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {successMessage}
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="alert alert-error">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M15 9l-6 6M9 9l6 6" />
            </svg>
            {error}
          </div>
        )}

        {/* Grading Modal */}
        {gradingSubmission && (
          <div className="grading-modal-overlay" onClick={handleCancelGrade}>
            <div className="grading-modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Grade Submission</h2>
                <button className="close-btn" onClick={handleCancelGrade}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="modal-content">
                <div className="student-info">
                  <div className="avatar">
                    {gradingSubmission.student_avatar ? (
                      <img src={gradingSubmission.student_avatar} alt="" />
                    ) : (
                      <span>{gradingSubmission.student_name?.charAt(0) || 'S'}</span>
                    )}
                  </div>
                  <div>
                    <p className="student-name">{gradingSubmission.student_name}</p>
                    <p className="student-email">{gradingSubmission.student_email}</p>
                  </div>
                </div>

                <div className="submission-content">
                  <h4>Submission</h4>
                  <p className="submission-date">Submitted: {formatDate(gradingSubmission.submitted_at)}</p>

                  {gradingSubmission.text_content && (
                    <div className="text-submission">
                      <pre>{gradingSubmission.text_content}</pre>
                    </div>
                  )}

                  {gradingSubmission.file_name && (
                    <div className="file-submission">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                        <path d="M14 2v6h6" />
                      </svg>
                      <span>{gradingSubmission.file_name}</span>
                      {gradingSubmission.file_size && (
                        <span className="file-size">({formatFileSize(gradingSubmission.file_size)})</span>
                      )}
                      {gradingSubmission.file_path && (
                        <a
                          href={`${api.defaults.baseURL}${gradingSubmission.file_path}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="download-link"
                        >
                          Download
                        </a>
                      )}
                    </div>
                  )}

                  {gradingSubmission.comments && (
                    <div className="student-comments">
                      <h5>Student Comments</h5>
                      <p>{gradingSubmission.comments}</p>
                    </div>
                  )}
                </div>

                <form onSubmit={handleGradeSubmit} className="grade-form">
                  <div className="form-group">
                    <label htmlFor="score">Score (out of {assignment.max_score})</label>
                    <input
                      type="number"
                      id="score"
                      min="0"
                      max={assignment.max_score}
                      step="0.5"
                      value={gradeForm.score}
                      onChange={(e) => setGradeForm({ ...gradeForm, score: e.target.value })}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="feedback">Feedback</label>
                    <textarea
                      id="feedback"
                      rows={4}
                      value={gradeForm.feedback}
                      onChange={(e) => setGradeForm({ ...gradeForm, feedback: e.target.value })}
                      placeholder="Provide feedback for the student..."
                    />
                  </div>

                  <div className="modal-actions">
                    <button type="button" className="btn-secondary" onClick={handleCancelGrade}>
                      Cancel
                    </button>
                    <button type="submit" className="btn-primary" disabled={saving}>
                      {saving ? 'Saving...' : 'Save Grade'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Submissions List */}
        {submissions.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <p>No submissions yet for this assignment.</p>
          </div>
        ) : (
          <div className="submissions-table">
            <div className="table-header">
              <span className="col-student">Student</span>
              <span className="col-submitted">Submitted</span>
              <span className="col-status">Status</span>
              <span className="col-score">Score</span>
              <span className="col-actions">Actions</span>
            </div>

            {submissions.map((submission) => (
              <div key={submission.id} className="table-row">
                <div className="col-student">
                  <div className="student-cell">
                    <div className="avatar">
                      {submission.student_avatar ? (
                        <img src={submission.student_avatar} alt="" />
                      ) : (
                        <span>{submission.student_name?.charAt(0) || 'S'}</span>
                      )}
                    </div>
                    <div className="student-details">
                      <p className="name">{submission.student_name}</p>
                      <p className="email">{submission.student_email}</p>
                    </div>
                  </div>
                </div>

                <div className="col-submitted">
                  <span>{formatDate(submission.submitted_at)}</span>
                </div>

                <div className="col-status">
                  <span className={`status-badge ${submission.status}`}>
                    {submission.status === 'submitted' ? 'Pending Review' : 'Graded'}
                  </span>
                </div>

                <div className="col-score">
                  {submission.status === 'graded' ? (
                    <span className="score">
                      {submission.score} / {assignment.max_score}
                    </span>
                  ) : (
                    <span className="no-score">-</span>
                  )}
                </div>

                <div className="col-actions">
                  <button
                    className="btn-grade"
                    onClick={() => handleGradeClick(submission)}
                  >
                    {submission.status === 'graded' ? 'Edit Grade' : 'Grade'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default GradeSubmissions;
